import { useCallback, useEffect, useState } from 'react';

import {
  BOARD_WAITING_REASON_LABELS,
  type TicketList,
  type TicketListRow,
} from '@nightshift/ui-types';

import { useTicketRescanMenu } from '../components/ticket-rescan-menu.tsx';
import { Empty, Kicker, RefreshError, Spinner, StateChip } from '../components/primitives.tsx';
import { api } from '../live/api.ts';
import { useLive } from '../live/context.tsx';
import { useTimeFormat } from '../lib/display.ts';
import { absoluteTime, observedTime } from '../lib/format.ts';
import { createLatestRequest, useDebouncedValue } from '../lib/latest-request.ts';
import { STATE_ORDER } from '../lib/palette.ts';
import {
  ticketsEmptyCopy,
  ticketsFilterActive,
  ticketsFilterFrom,
  waitingReasonLabel,
  type TicketsFilter,
} from '../lib/tickets-view.ts';
import type { ViewProps } from './board.tsx';
import './sheet.css';
import './tickets.css';

function repairLabel(row: TicketListRow): string | null {
  let used = 0;
  let max = 5;
  for (const entry of row.repairs) {
    if (entry.used > used) {
      used = entry.used;
      max = entry.max;
    }
  }
  if (used === 0) return null;
  return `${used}/${max}`;
}

/**
 * Filterable ticket index — every ticket, including Waiting and Cancelled.
 *
 * Project, phase and milestone come from the top bar. State, waiting reason, and text
 * live in the URL so board rail rows can deep-link here.
 */
export function TicketsView({
  onOpenTicket,
  filter,
  onChangeFilter,
}: ViewProps & {
  filter: TicketsFilter;
  onChangeFilter: (next: TicketsFilter) => void;
}) {
  const live = useLive();
  const [page, setPage] = useState<TicketList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loadedAt, setLoadedAt] = useState<number | null>(null);
  const [latest] = useState(createLatestRequest);

  const projectSlug = live.projectSlug;
  const milestoneId = live.milestoneId;
  const phaseId = live.phaseId;
  const poke = `${live.board?.generatedAt ?? 0}:${live.snapshot?.revision ?? 0}`;
  const parsed = ticketsFilterFrom(filter);
  // The box shows every keystroke; the server is asked once typing pauses.
  const q = useDebouncedValue(parsed.q, 250);

  const load = useCallback(async () => {
    const request = latest.begin();
    setBusy(true);
    try {
      const next = await api.tickets({
        project: projectSlug,
        milestone: milestoneId,
        phase: phaseId,
        state: parsed.state,
        reason: parsed.reason,
        q,
      });
      if (!latest.isCurrent(request)) return;
      setPage(next);
      setLoadedAt(Date.now());
      setError(null);
    } catch (caught) {
      if (!latest.isCurrent(request)) return;
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      if (latest.isCurrent(request)) setBusy(false);
    }
  }, [latest, projectSlug, milestoneId, phaseId, parsed.state, parsed.reason, q]);

  useEffect(() => {
    void load();
  }, [load, poke]);

  useEffect(() => () => latest.cancel(), [latest]);

  const filterActive = ticketsFilterActive(parsed);
  const empty = ticketsEmptyCopy({ filterActive });

  if (error && !page) {
    return (
      <Empty
        title="The ticket list could not be read."
        body={error}
        action={
          <button type="button" className="btn" onClick={() => void load()}>
            Try again
          </button>
        }
      />
    );
  }

  if (!page) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', flex: 1 }}>
        <Spinner size={20} />
      </div>
    );
  }

  const patch = (next: Partial<TicketsFilter>) =>
    onChangeFilter(ticketsFilterFrom({ ...parsed, ...next }));

  return (
    <div className="sheet tickets-index" aria-busy={busy}>
      <div className="sheet-bar">
        <Kicker count={page.items.length}>Tickets</Kicker>
        <label className="picker topbar-filter">
          <span className="topbar-filter-label">State</span>
          <select
            value={parsed.state ?? ''}
            onChange={(event) => patch({ state: event.target.value || null })}
            aria-label="State"
          >
            <option value="">All states</option>
            {STATE_ORDER.map((state) => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </select>
        </label>
        <label className="picker topbar-filter">
          <span className="topbar-filter-label">Reason</span>
          <select
            value={parsed.reason ?? ''}
            onChange={(event) => patch({ reason: event.target.value || null })}
            aria-label="Waiting reason"
          >
            <option value="">All reasons</option>
            {Object.entries(BOARD_WAITING_REASON_LABELS).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="tickets-search">
          <span className="topbar-filter-label">Search</span>
          <input
            type="search"
            value={parsed.q ?? ''}
            placeholder="Key, title, waiting reason"
            aria-label="Search tickets"
            onChange={(event) => patch({ q: event.target.value || null })}
          />
        </label>
        {filterActive ? (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => onChangeFilter(ticketsFilterFrom({}))}
          >
            Clear filters
          </button>
        ) : null}
      </div>
      {error ? (
        <RefreshError
          what="The ticket list"
          error={error}
          since={loadedAt}
          now={live.tick}
          onRetry={() => void load()}
        />
      ) : null}
      <div className="sheet-body">
        {page.items.length === 0 ? (
          <Empty title={empty.title} body={empty.body} />
        ) : (
          <div className="sheet-panel">
            <table className="table">
              <thead>
                <tr>
                  <th>Key</th>
                  <th>Title</th>
                  <th>State</th>
                  <th>Waiting</th>
                  <th>Milestone</th>
                  <th>Tags</th>
                  <th>Repair</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {page.items.map((row) => (
                  <TicketRow
                    key={row.id}
                    row={row}
                    now={live.tick}
                    onOpen={onOpenTicket}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function TicketRow({
  row,
  now,
  onOpen,
}: {
  row: TicketListRow;
  now: number;
  onOpen: (key: string) => void;
}) {
  const format = useTimeFormat();
  const rescan = useTicketRescanMenu(row.ticketKey);
  const repair = repairLabel(row);
  const waiting = row.waitingReason
    ? waitingReasonLabel(row.waitingReason)
    : row.waitingReasonText;
  const tags = row.tags.length > 0 ? row.tags.join(', ') : null;

  return (
    <>
    {rescan.menu}
    <tr
      onContextMenu={rescan.onContextMenu}
      className="tickets-row"
      tabIndex={0}
      onClick={() => onOpen(row.ticketKey)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen(row.ticketKey);
        }
      }}
      aria-label={`${row.ticketKey} — ${row.title}, ${row.state}`}
    >
      <td className="mono tickets-key">{row.ticketKey}</td>
      <td className="tickets-title">{row.title}</td>
      <td>
        <StateChip state={row.state} />
        {row.activity ? (
          <span className="dim" style={{ marginLeft: 6, fontSize: 'var(--fs-micro)' }}>
            {row.activity}
          </span>
        ) : null}
      </td>
      <td>
        {waiting ? waiting : <span className="tickets-muted">—</span>}
      </td>
      <td>
        {row.milestoneName ? row.milestoneName : <span className="tickets-muted">—</span>}
      </td>
      <td>
        {tags ? tags : <span className="tickets-muted">—</span>}
      </td>
      <td className="num">
        {repair ? repair : <span className="tickets-muted">—</span>}
      </td>
      <td className="num" title={absoluteTime(row.updatedAt)}>
        {observedTime(row.updatedAt, now, format)}
      </td>
    </tr>
    </>
  );
}
